import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  Autocomplete,
  Chip,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Contact as DbContact, Organization } from '../../types/database';
import { useDatabase } from '../../hooks/useDatabase';
import { OrganizationModal } from '../organizations/OrganizationModal';

export type Contact = DbContact;

interface ContactModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (contact: Partial<Omit<Contact, 'id' | 'created_at' | 'updated_at' | 'relevance_score'>>, organizationId?: string | null) => void;
  contact?: Contact | null;
}

export const ContactModal: React.FC<ContactModalProps> = ({
  open,
  onClose,
  onSubmit,
  contact,
}) => {
  const db = useDatabase();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    description: '',
    industries: [] as string[],
    industry: '',
    skills: [] as string[],
    tags: {} as Record<string, boolean>,
    user_id: '',
    associated_contacts: [] as string[],
    relationship_types: {} as Record<string, string>,
    linkedin_url: '',
    twitter_url: '',
    instagram_url: '',
    facebook_url: '',
    github_url: '',
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState<Organization[]>([]);
  const [organizationInputValue, setOrganizationInputValue] = useState('');
  const [organizationModalOpen, setOrganizationModalOpen] = useState(false);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [newOrganizationName, setNewOrganizationName] = useState('');

  // Generate a unique key for localStorage based on contact ID
  const getStorageKey = useCallback(() => {
    return contact ? `contact_edit_${contact.id}` : 'contact_edit_new';
  }, [contact]);

  // Load organizations when modal opens
  useEffect(() => {
    if (!open) return;
    
    const loadOrganizations = async () => {
      setLoadingOrganizations(true);
      try {
        const { data, error } = await db.getOrganizations();
        if (error) {
          console.error('Error loading organizations:', error);
        } else {
          setOrganizations(data || []);
        }
      } catch (err) {
        console.error('Error loading organizations:', err);
      } finally {
        setLoadingOrganizations(false);
      }
    };

    loadOrganizations();
  }, [open, db]);

  // Load contact's organizations when editing
  useEffect(() => {
    if (!open || !contact) {
      setSelectedOrganizations([]);
      return;
    }

    const loadContactOrganizations = async () => {
      try {
        const { data, error } = await db.getContactOrganizations(contact.id);
        if (!error && data) {
          setSelectedOrganizations(data);
        } else {
          setSelectedOrganizations([]);
        }
      } catch (err) {
        console.error('Error loading contact organizations:', err);
        setSelectedOrganizations([]);
      }
    };

    loadContactOrganizations();
  }, [open, contact, db]);

  // Load form data from contact when modal opens
  useEffect(() => {
    if (!open) {
      // Reset form when modal closes
      setFormData({
        name: '',
        email: '',
        phone: '',
        description: '',
        industries: [],
        industry: '',
        skills: [],
        tags: {},
        user_id: '',
        associated_contacts: [],
        relationship_types: {},
        linkedin_url: '',
        twitter_url: '',
        instagram_url: '',
        facebook_url: '',
        github_url: '',
      });
      setHasUnsavedChanges(false);
      return;
    }
    
    if (contact) {
      // Always populate from contact data when editing
      const initialData = {
        name: contact.name || '',
        email: contact.email || '',
        phone: contact.phone || '',
        description: contact.description || '',
        industries: contact.industries || [],
        industry: contact.industry || '',
        skills: contact.skills || [],
        tags: contact.tags || {},
        user_id: contact.user_id || '',
        // Will be handled separately via junction table
        associated_contacts: contact.associated_contacts || [],
        relationship_types: contact.relationship_types || {},
        linkedin_url: contact.linkedin_url || '',
        twitter_url: contact.twitter_url || '',
        instagram_url: contact.instagram_url || '',
        facebook_url: contact.facebook_url || '',
        github_url: contact.github_url || '',
      };
      
      setFormData(initialData);
      setHasUnsavedChanges(false);
      
      // Clear any saved data for this contact when opening
      const storageKey = getStorageKey();
      localStorage.removeItem(storageKey);
    } else {
      // For new contacts, start with empty form
      setFormData({
        name: '',
        email: '',
        phone: '',
        description: '',
        industries: [],
        industry: '',
        skills: [],
        tags: {},
        user_id: '',
        associated_contacts: [],
        relationship_types: {},
        linkedin_url: '',
        twitter_url: '',
        instagram_url: '',
        facebook_url: '',
        github_url: '',
      });
      setHasUnsavedChanges(false);
      
      // Clear any saved data for new contact
      const storageKey = getStorageKey();
      localStorage.removeItem(storageKey);
    }
  }, [open, contact, getStorageKey]); // Re-run when modal opens/closes or contact changes

  // Auto-save form data to localStorage (only when modal is open and user has made changes)
  useEffect(() => {
    if (!open) return;
    
    const storageKey = getStorageKey();
    // Only save if there are actual changes (not just initial load)
    if (hasUnsavedChanges) {
      localStorage.setItem(storageKey, JSON.stringify(formData));
    }
  }, [formData, getStorageKey, open, hasUnsavedChanges]);

  // Clean up localStorage when modal closes successfully
  const handleClose = () => {
    const storageKey = getStorageKey();
    localStorage.removeItem(storageKey);
    setHasUnsavedChanges(false);
    onClose();
  };

  const handleFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setHasUnsavedChanges(true);
  };

  const handleCreateOrganization = async (organizationData: Omit<Organization, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data: newOrg, error } = await db.createOrganization(organizationData);
      if (error) throw error;
      if (newOrg) {
        setOrganizations(prev => [...prev, newOrg]);
        // Add to selected organizations if not already there
        setSelectedOrganizations(prev => {
          if (prev.some(org => org.id === newOrg.id)) {
            return prev;
          }
          return [...prev, newOrg];
        });
        setOrganizationModalOpen(false);
        setHasUnsavedChanges(true);
      }
    } catch (err) {
      console.error('Error creating organization:', err);
      alert('Failed to create organization. Please try again.');
    }
  };

  const handleRemoveOrganization = (organizationId: string) => {
    setSelectedOrganizations(prev => prev.filter(org => org.id !== organizationId));
    setHasUnsavedChanges(true);
  };

  const handleAddOrganization = (organization: Organization) => {
    // Check if organization is already selected
    if (!selectedOrganizations.some(org => org.id === organization.id)) {
      setSelectedOrganizations(prev => [...prev, organization]);
      setOrganizationInputValue('');
      setHasUnsavedChanges(true);
    }
  };

  const handleSubmit = async () => {
    console.log('Submitting form data:', formData);
    const contactData = {
      name: formData.name?.trim() || '',
      email: formData.email?.trim() || '',
      phone: formData.phone?.trim() || '',
      description: formData.description?.trim() || '',
      industry: formData.industry?.trim() || undefined,
      industries: formData.industries || [],
      skills: formData.skills || [],
      tags: formData.tags || {},
      linkedin_url: formData.linkedin_url?.trim() || undefined,
      twitter_url: formData.twitter_url?.trim() || undefined,
      instagram_url: formData.instagram_url?.trim() || undefined,
      facebook_url: formData.facebook_url?.trim() || undefined,
      github_url: formData.github_url?.trim() || undefined,
      // Don't include user_id, associated_contacts, or relationship_types in update
      // These are managed separately or shouldn't be changed via this form
    };
    console.log('Processed contact data:', contactData);
    
    // Clean up localStorage before submitting
    const storageKey = getStorageKey();
    localStorage.removeItem(storageKey);
    setHasUnsavedChanges(false);
    
    // Handle organization linking/unlinking if contact exists
    if (contact) {
      // For editing: submit contact data first, then handle organizations separately
      onSubmit(contactData, null);
      
      // Get current organizations
      const { data: currentOrgs } = await db.getContactOrganizations(contact.id);
      const currentOrgIds = (currentOrgs || []).map(org => org.id);
      const selectedOrgIds = selectedOrganizations.map(org => org.id);
      
      // Unlink organizations that are no longer selected
      for (const orgId of currentOrgIds) {
        if (!selectedOrgIds.includes(orgId)) {
          await db.unlinkContactFromOrganization(contact.id, orgId);
        }
      }
      
      // Link new organizations
      for (const org of selectedOrganizations) {
        if (!currentOrgIds.includes(org.id)) {
          await db.linkContactToOrganization(contact.id, org.id);
        }
      }
    } else {
      // For new contacts: only call onSubmit once with the first organization ID if available
      // The parent component will handle linking the organization after contact creation
      const organizationId = selectedOrganizations.length > 0 ? selectedOrganizations[0].id : null;
      onSubmit(contactData, organizationId);
      
      // Note: Additional organizations beyond the first one will need to be linked separately
      // by the parent component after the contact is created
    }
    
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={hasUnsavedChanges ? () => {
        if (window.confirm('You have unsaved changes. Are you sure you want to close without saving?')) {
          handleClose();
        }
      } : handleClose} 
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle>
        {contact ? 'Edit Contact' : 'Add New Contact'}
        {hasUnsavedChanges && (
          <span style={{ fontSize: '0.8em', color: '#ff9800', marginLeft: '10px' }}>
            (Unsaved changes)
          </span>
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleFormChange}
            required
          />
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleFormChange}
          />
          <TextField
            fullWidth
            label="Phone"
            name="phone"
            value={formData.phone}
            onChange={handleFormChange}
          />
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleFormChange}
            multiline
            rows={4}
            placeholder="Tell us about this contact..."
          />
          <TextField
            fullWidth
            label="Industry"
            name="industry"
            value={formData.industry}
            onChange={handleFormChange}
          />
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Organizations
            </Typography>
            {selectedOrganizations.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {selectedOrganizations.map((org) => (
                  <Chip
                    key={org.id}
                    label={org.name}
                    onDelete={() => handleRemoveOrganization(org.id)}
                    deleteIcon={<DeleteIcon />}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
            <Autocomplete
              options={organizations.filter(org => !selectedOrganizations.some(selected => selected.id === org.id))}
              getOptionLabel={(option) => option.name}
              value={null}
              onChange={(event, newValue) => {
                if (newValue) {
                  handleAddOrganization(newValue);
                }
              }}
              inputValue={organizationInputValue}
              onInputChange={(event, newInputValue) => {
                setOrganizationInputValue(newInputValue);
              }}
              loading={loadingOrganizations}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add Organization"
                  placeholder="Search or create organization..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {params.InputProps.endAdornment}
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOrganizationModalOpen(true);
                          }}
                          sx={{ mr: 1 }}
                        >
                          New
                        </Button>
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Box>
                    <Typography variant="body1">{option.name}</Typography>
                    {option.industry && (
                      <Typography variant="caption" color="text.secondary">
                        {option.industry}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText={
                organizationInputValue.trim() ? (
                  <Box sx={{ p: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      No organization found. Create a new one?
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setNewOrganizationName(organizationInputValue.trim());
                        setOrganizationModalOpen(true);
                      }}
                      fullWidth
                    >
                      Create "{organizationInputValue}"
                    </Button>
                  </Box>
                ) : (
                  'Start typing to search organizations'
                )
              }
              freeSolo={false}
              clearOnBlur
              selectOnFocus
              handleHomeEndKeys
            />
          </Box>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
            Social Media
          </Typography>
          <TextField
            fullWidth
            label="LinkedIn URL"
            name="linkedin_url"
            type="url"
            value={formData.linkedin_url}
            onChange={handleFormChange}
            placeholder="https://linkedin.com/in/username"
          />
          <TextField
            fullWidth
            label="Twitter/X URL"
            name="twitter_url"
            type="url"
            value={formData.twitter_url}
            onChange={handleFormChange}
            placeholder="https://twitter.com/username"
          />
          <TextField
            fullWidth
            label="Instagram URL"
            name="instagram_url"
            type="url"
            value={formData.instagram_url}
            onChange={handleFormChange}
            placeholder="https://instagram.com/username"
          />
          <TextField
            fullWidth
            label="Facebook URL"
            name="facebook_url"
            type="url"
            value={formData.facebook_url}
            onChange={handleFormChange}
            placeholder="https://facebook.com/username"
          />
          <TextField
            fullWidth
            label="GitHub URL"
            name="github_url"
            type="url"
            value={formData.github_url}
            onChange={handleFormChange}
            placeholder="https://github.com/username"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={!formData.name}
        >
          {contact ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
      
      {/* Organization Creation Modal */}
      <OrganizationModal
        open={organizationModalOpen}
        onClose={() => {
          setOrganizationModalOpen(false);
          setNewOrganizationName('');
        }}
        onSubmit={(orgData) => {
          // If we have a pre-filled name, use it
          const organizationData = newOrganizationName 
            ? { ...orgData, name: newOrganizationName }
            : orgData;
          handleCreateOrganization(organizationData);
          setNewOrganizationName('');
        }}
        organization={newOrganizationName ? { 
          id: '', 
          created_at: '', 
          updated_at: '', 
          name: newOrganizationName, 
          user_id: '',
          description: '',
          website: '',
          industry: '',
          size: '',
          location: ''
        } : null}
      />
    </Dialog>
  );
}; 